import React, { useEffect, useRef } from "react";
import {
  useNavigate,
  useLocation,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import Header from "../components/HomeHeader/Header";
import { SKY_FADE, SKY_FADE_BAND_HEIGHT } from "../components/AppBackground";
import { StreakCalendarProvider } from "../components/Streak/StreakCalendarContext";
import {
  NavVisibilityProvider,
  useNavHidden,
} from "../context/NavVisibilityContext";
import DictionaryPage from "./DictionaryPage";
import CoursesPage from "./CoursesPage";
import LearnPage from "./LearnPage";
import NavBar from "../components/NavBar/NavBar";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { setPostHogResetFunction } from "@/api/login";
import ProfilePage from "./ProfilePage";
import FeedPage from "./FeedPage";
import { ActivePage } from "../components/NavBar/NavBar";

/** Bottom nav that hides itself when a full-screen child opts out (e.g. the
 *  Parrot Method carousel). Lives under NavVisibilityProvider so the hook is
 *  always valid. */
const BottomNav: React.FC<{
  navigate: ReturnType<typeof useNavigate>;
  activePage: ActivePage;
  isFeed: boolean;
}> = ({ navigate, activePage, isFeed }) => {
  const hidden = useNavHidden();
  if (hidden) return null;
  return (
    <nav className="fixed bottom-0 left-0 w-full z-10">
      <NavBar
        navigate={navigate}
        activePage={activePage}
        variant={isFeed ? "dark" : "light"}
      />
    </nav>
  );
};

const HomeLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });

  const runOnce = useRef(false);
  const posthog = usePostHog();

  // Scroll to the top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const distinctId = posthog.get_distinct_id();
    const alreadyIdentified = userInfo.data?.id.toString() === distinctId;
    const isTemp =
      userInfo.data?.isTemp ||
      userInfo.data?.isTempTrial ||
      userInfo.data?.isTempPremium;
    if (userInfo.data && !runOnce.current && !alreadyIdentified && !isTemp) {
      runOnce.current = true;
      posthog.identify(userInfo.data.id.toString(), {
        name: userInfo.data.firstName,
        email: userInfo.data.email,
        isTrial: userInfo.data.isTrial,
        isPremium: userInfo.data.isPremium,
        isFree: userInfo.data.isFree,
      });
      setPostHogResetFunction(() => {
        if (posthog) {
          posthog.reset();
          try {
            posthog.stopSessionRecording();
          } catch (error) {
            console.error(error);
          }
        }
      });
    }
    // Session replay is opt-in behind the same `session_replay_enabled` flag
    // iOS gates on (HomeScreen.tsx:43-56): record only when the flag is
    // strictly enabled (default OFF), stop otherwise.
    try {
      if (posthog.isFeatureEnabled("session_replay_enabled")) {
        posthog.startSessionRecording();
      } else {
        posthog.stopSessionRecording();
      }
    } catch (error) {
      console.error(error);
    }
    // `runOnce` guards re-identification; re-running once the userInfo query
    // resolves is what lets the identify branch fire at all.
  }, [userInfo.data, posthog]);

  const getActivePage = (): ActivePage => {
    const path = location.pathname.split("/").pop();
    const activePages: ActivePage[] = [
      "learn",
      "dictionary",
      "profile",
      "courses",
      "feed",
    ];
    if (activePages.includes(path as ActivePage)) {
      return path as ActivePage;
    }
    console.warn(`Invalid path: ${path}`);
    return "learn";
  };

  const activePage = getActivePage();
  const isFeed = activePage === "feed";

  if (userInfo.data?.isTemp) {
    return <Navigate to="/" />;
  }

  const isHome = activePage === "learn";

  // The page BODY. Feed gets its own full-screen subtree; every other tab
  // shares the appPadding column. This subtree is allowed to unmount/remount
  // across the feed boundary (FeedPage must tear down) — the NavBar below is
  // kept OUT of it so it survives the swap.
  const body = isFeed ? (
    <div className="fixed inset-0 flex flex-col">
      {/* `relative z-0` makes the feed content its own stacking context so the
          locked overlay (`absolute inset-0 z-50`) is trapped inside it and
          can't paint over the nav. The fixed nav (rendered as a sibling below)
          sits in a higher context so it stays visible/tappable. See #8. */}
      <div className="flex-1 min-h-0 relative z-0">
        <FeedPage />
      </div>
    </div>
  ) : (
    <div className="appPadding flex flex-col h-full w-full pt-1">
      {/* Profile has its own gear/share header inside its sky banner (iOS parity),
          so the shared top Header is suppressed there to avoid a redundant bar. */}
      {activePage !== "profile" && (
        <div
          // Fixed overlay strip on every tab, painted with the SAME gradient
          // as the tab's backdrop — sized to the backdrop's full extent and
          // clipped to the bar — so its pixels are identical to the sky behind
          // it (iOS AppTopBar has no background of its own; it sits on the
          // screen color). Opaque (white base layer under the translucent fade)
          // so content scrolling underneath disappears behind it instead of
          // colliding with the streak pill / wordmark.
          // On Home the bar is the BOTTOM layer (iOS: header z0, path sheet
          // z10 on top) so the white course sheet rises OVER it as you scroll;
          // the streak pill stays tappable because the sheet's transparent top
          // is pointer-events:none. Other tabs keep it on top (z-10) to mask
          // their scrolling content.
          className={`fixed top-0 left-0 w-full h-16 items-center ${
            isHome ? "z-[5]" : "z-10"
          }`}
          style={
            isHome
              ? {
                  // Transparent so the sky + clouds behind (SkyBackground)
                  // show through the bar instead of being masked by a
                  // cloudless gradient — iOS AppTopBar sits directly on the
                  // sky. Safe here because the home hero is pinned BELOW the
                  // 64px bar (sticky top:64), so nothing scrolls up into the
                  // bar to collide with the streak pill / wordmark.
                  background: "transparent",
                }
              : activePage === "dictionary"
                ? {
                    backgroundImage: SKY_FADE,
                    backgroundSize: `100% ${SKY_FADE_BAND_HEIGHT}px`,
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#ffffff",
                  }
                : { background: "#fafafa" } // courses page background
          }
        >
          <Header activePage={activePage} />
        </div>
      )}
      <div className="flex flex-col justify-top w-full pb-24">
        <Routes>
          {/* Bare /home lands on the Home (learn) tab, matching iOS which
              opens the Home tab content by default. */}
          <Route index element={<Navigate to="learn" replace />} />
          <Route path="learn" element={<LearnPage />} />
          <Route path="dictionary" element={<DictionaryPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="courses" element={<CoursesPage />} />
        </Routes>
      </div>
    </div>
  );

  // ONE persistent <NavBar>, always the SECOND child of this Fragment so React
  // never unmounts/remounts it as `body` (child 0) swaps across the feed
  // boundary. Because the instance is preserved, the `variant` prop flips
  // IN-PLACE (light↔dark) and the bar bg/border + per-button tints TWEEN via
  // CSS `transition-colors` over 500ms instead of a hard DOM-node swap (#4).
  // The active-pill slide is likewise handled by the in-mount framer spring
  // (its `animate` target moving to the newly-active tab), not a remount.
  return (
    // Provider wraps BOTH the body and the persistent NavBar so the nav tabs
    // (rendered outside `body` to survive the feed swap) can call close() to
    // dismiss the streak overlay before switching tabs — event-driven, no
    // route-change effect. Feed is inside it too but never opens the overlay.
    // NavVisibilityProvider additionally lets a full-screen child (the Parrot
    // Method carousel) hide the bottom nav while it's mounted.
    <StreakCalendarProvider>
      <NavVisibilityProvider>
        {body}
        <BottomNav navigate={navigate} activePage={activePage} isFeed={isFeed} />
      </NavVisibilityProvider>
    </StreakCalendarProvider>
  );
};

export default HomeLayout;
